import { markRaw } from 'vue'
import type { Component } from 'vue'
import IconBus from '~icons/mdi/bus'
import IconClothes from '~icons/mdi/tshirt-crew-outline'
import IconDocuments from '~icons/mdi/file-document-outline'
import IconFood from '~icons/mdi/food-apple-outline'
import IconHome from '~icons/mdi/home-outline'
import IconLeisure from '~icons/mdi/ticket-outline'
import IconLipstick from '~icons/mdi/lipstick'
import IconOther from '~icons/mdi/dots-horizontal'
import IconPaw from '~icons/mdi/paw-outline'
import IconPill from '~icons/mdi/pill'
import IconSofa from '~icons/mdi/sofa-outline'
import IconTag from '~icons/mdi/tag-outline'
import IconWifi from '~icons/mdi/wifi'
import IconCafe from '~icons/mdi/silverware-fork-knife'
import IconCart from '~icons/mdi/cart-outline'
import IconBank from '~icons/mdi/bank-outline'
import IconBar from '~icons/mdi/glass-cocktail'
import IconBooks from '~icons/mdi/book-open-variant'
import IconCar from '~icons/mdi/car-outline'
import IconCharity from '~icons/mdi/hand-heart-outline'
import IconCoffee from '~icons/mdi/coffee-outline'
import IconDentist from '~icons/mdi/tooth-outline'
import IconFlowers from '~icons/mdi/flower-outline'
import IconFuel from '~icons/mdi/gas-station-outline'
import IconGames from '~icons/mdi/gamepad-variant-outline'
import IconGift from '~icons/mdi/gift-outline'
import IconKids from '~icons/mdi/baby-carriage'
import IconLanguages from '~icons/mdi/translate'
import IconMusic from '~icons/mdi/music-note'
import IconPhone from '~icons/mdi/cellphone'
import IconRepair from '~icons/mdi/hammer-wrench'
import IconSport from '~icons/mdi/dumbbell'
import IconStudy from '~icons/mdi/school-outline'
import IconSubscriptions from '~icons/mdi/autorenew'
import IconTaxi from '~icons/mdi/taxi'
import IconTech from '~icons/mdi/laptop'
import IconTravel from '~icons/mdi/airplane'
import IconUtilities from '~icons/mdi/lightning-bolt-outline'
import {
  DEFAULT_SPENDING_ICON,
  MINOR_EXPONENT,
  RATE_SCALE,
  currencySign,
  decimalFromRate,
  drawsNothing,
  formatEstimate,
  formatMoney,
  formatRate,
  homeBankOf,
  isRateDay,
  nextCategoryColour,
  TRIP_CATEGORY,
} from '@molvia/model'
import type {
  Currency,
  ExchangeRate,
  Money,
  MoneyMonthView,
  SpendingCategoryView,
  SpendingIcon,
  SpendingPreset,
  SpendingView,
} from '@molvia/model'
import type {
  ArrivedCategoryWrite,
  RejectedSpendingWrite,
  SpendingUndo,
  SpendingWrite,
} from '@/stores/spendingQueue'
import { spendingOf } from '@/stores/spendingQueue'
import type { SpendingPrefill } from '@/stores/spendingHandoff'
import { calendarDayOfAnyYear, dayWords, shiftDay } from '@/days'

/**
 * What the screen of «Деньги» draws, put together from the server's month and the phone's queue
 * (MOL-82). Nothing here adds money up: every total is the server's, and a spending still in the
 * queue is a row with a mark, never a figure in a sum (CLAUDE.md, «no business logic on the
 * frontend»; requirements, Р-3).
 */

/** The icon of a preset, by its key (handoff 06). */
const PRESET_ICONS: Record<SpendingPreset, Component> = {
  groceries: markRaw(IconFood),
  cafe: markRaw(IconCafe),
  rent: markRaw(IconHome),
  home: markRaw(IconSofa),
  beauty: markRaw(IconLipstick),
  transport: markRaw(IconBus),
  telecom: markRaw(IconWifi),
  health: markRaw(IconPill),
  clothes: markRaw(IconClothes),
  pets: markRaw(IconPaw),
  leisure: markRaw(IconLeisure),
  documents: markRaw(IconDocuments),
  other: markRaw(IconOther),
}

/**
 * What one's own category may be drawn with (MOL-285): the tag, the owner's twenty-three and the
 * presets' own, by the domain's key — the drawing is the phone's, so an icon may be redrawn here
 * without a migration.
 */
export const OWN_ICONS: Record<SpendingIcon, Component> = {
  tag: markRaw(IconTag),
  gift: markRaw(IconGift),
  sport: markRaw(IconSport),
  study: markRaw(IconStudy),
  kids: markRaw(IconKids),
  taxi: markRaw(IconTaxi),
  car: markRaw(IconCar),
  fuel: markRaw(IconFuel),
  travel: markRaw(IconTravel),
  coffee: markRaw(IconCoffee),
  bar: markRaw(IconBar),
  games: markRaw(IconGames),
  books: markRaw(IconBooks),
  music: markRaw(IconMusic),
  phone: markRaw(IconPhone),
  tech: markRaw(IconTech),
  repair: markRaw(IconRepair),
  flowers: markRaw(IconFlowers),
  charity: markRaw(IconCharity),
  subscriptions: markRaw(IconSubscriptions),
  bank: markRaw(IconBank),
  languages: markRaw(IconLanguages),
  dentist: markRaw(IconDentist),
  utilities: markRaw(IconUtilities),
  ...PRESET_ICONS,
}

/** A preset by its key; one's own by the icon it chose — an answer kept from before that, the tag. */
export function categoryIcon(category: SpendingCategoryView): Component {
  if (category.preset) return PRESET_ICONS[category.preset]
  return OWN_ICONS[category.icon ?? DEFAULT_SPENDING_ICON]
}

/**
 * A rate in words — «4,62 ֏ за 1 ₽»: how much of one currency a unit of the other costs, on the
 * side whose number is at least one, as «Деньги» keeps every rate (MOL-73, С-1). A rate that came
 * the other way round is printed as the domain prints any rate, rather than turned over here.
 */
export function rateWords(
  rate: ExchangeRate,
  locale: string,
  t: (key: string, named: Record<string, unknown>) => string,
): string {
  if (rate.scaled < RATE_SCALE) return formatRate(rate, locale)
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(decimalFromRate(rate.scaled))
  return t('spending.rate_value', {
    amount: `${number} ${currencySign(rate.quote, locale)}`,
    sign: currencySign(rate.base, locale),
  })
}

/**
 * The bank a pair's official rate is named by — «ЦБ РА», «НБ Грузии» (MOL-110, owner's decision
 * В-2: the source is called by its bank): the domain's `homeBankOf`, by the name an exchange's card
 * gives it, the one that stands at the head of a line and after «the» alike.
 */
export function bankWords(base: Currency, quote: Currency, t: (key: string) => string): string {
  return t(`exchange.card_source_${homeBankOf(base, quote)}`)
}

/** The colour of a category as a custom property — the tokens decide which colour it is. */
export function categoryColour(category: SpendingCategoryView): string {
  return category.preset
    ? `var(--cat-${category.preset})`
    : `var(--cat-own-${String(category.colour ?? 0)})`
}

/**
 * The categories of a spending's chips by frequency (MOL-198, Ф-17): the frequent ones the server named,
 * then the rest as the list has them — a newcomer's, the presets as listed. The chosen one first is the
 * sheet's (`SpendingSheet`, owner's В-2 «а»), since only the sheet knows when the chips may move.
 */
export function chipOrder(
  categories: readonly SpendingCategoryView[],
  frequent: readonly string[],
): SpendingCategoryView[] {
  const rank = (id: string) => {
    const at = frequent.indexOf(id)
    return at === -1 ? frequent.length : at
  }
  // A stable sort: the rest keep the list's order.
  return [...categories].sort((a, b) => rank(a.id) - rank(b.id))
}

/** How many chips stand in the first row and in the second, «Ещё…» last in it (MOL-198, Ф-17). */
export interface ChipRows {
  readonly first: number
  readonly second: number
}

/**
 * The chips laid out in two rows by their widths as the browser measured them (MOL-198, Ф-17): a row
 * takes the next chip while it fits whole, the second keeping room for «Ещё…», and stops at the first
 * that does not — the order is never skipped through. The first chip always stands, cut by its own «…»
 * when its name is wider than the row: a category of forty letters at 320.
 */
export function chipsInTwoRows(
  widths: readonly number[],
  row: number,
  more: number,
  gap: number,
): ChipRows {
  let at = 0
  let taken = 0
  while (at < widths.length) {
    const next = taken === 0 ? 0 : taken + gap
    const width = widths[at] ?? 0
    if (taken > 0 && next + width > row) break
    taken = next + width
    at++
  }
  const first = at
  const room = row - gap - more
  taken = 0
  while (at < widths.length) {
    const next = taken === 0 ? 0 : taken + gap
    if (next + (widths[at] ?? 0) > room) break
    taken = next + (widths[at] ?? 0)
    at++
  }
  return { first, second: at - first }
}

/**
 * The person's categories as the phone knows them now: the server's list, then what waits in the
 * queue — a category made with no signal, an amendment (MOL-285), and a removal or a return not yet
 * sent (В-4). One's own made on the phone stands after the others, as the server will put it.
 */
export function categoriesWith(
  server: readonly SpendingCategoryView[],
  pending: readonly (SpendingWrite | ArrivedCategoryWrite)[],
  /** When the server's list was asked for: an amendment that landed before it is in it already. */
  askedAt: Date | null = null,
): SpendingCategoryView[] {
  const list = [...server]
  for (const write of pending) {
    if (write.kind === 'category-add' && !list.some((one) => one.id === write.body.id)) {
      // The server's own rule, so the colour does not change when the answer comes (review Т-11).
      list.push({
        id: write.body.id,
        preset: null,
        name: write.body.name,
        colour: write.body.colour ?? nextCategoryColour(list),
        icon: write.body.icon ?? DEFAULT_SPENDING_ICON,
        archived: false,
      })
    }
    const heard = 'landedAt' in write && askedAt !== null && askedAt.getTime() >= write.landedAt
    if (write.kind === 'category-amend' && !heard) {
      const at = list.findIndex((one) => one.id === write.id)
      const found = list[at]
      if (found?.preset === null) list[at] = { ...found, ...write.body }
    }
    if (write.kind === 'category-archive' || write.kind === 'category-restore') {
      const archived = write.kind === 'category-archive'
      const at = list.findIndex((one) => one.id === write.id)
      const found = list[at]
      if (found) list[at] = { ...found, archived }
    }
  }
  return list
}

/**
 * The journal read a page at a time: the next page continues where the last stopped, and a day
 * cut by the page comes back with the rest of its rows. Its total is the whole day's on both pages
 * (MOL-73), so the later one is simply the same figure.
 */
export function mergePages(first: MoneyMonthView, next: MoneyMonthView): MoneyMonthView {
  const days = first.days.map((day) => ({ ...day, entries: [...day.entries] }))
  for (const day of next.days) {
    const last = days.at(-1)
    if (last?.day === day.day) last.entries.push(...day.entries)
    else days.push({ ...day, entries: [...day.entries] })
  }
  return {
    ...first,
    days,
    cursor: next.cursor,
    remaining: next.remaining,
    remainingFrom: next.remainingFrom,
    remainingTo: next.remainingTo,
  }
}

/**
 * The month's spending in the income currency, «≈ 63 800 ₽» under «274 523 ֏» — one rule for «Деньги»
 * and «Траты» (MOL-184): only in another currency, since one who earns and spends in drams was told
 * «≈ 5 000 ֏» under exact drams (adversarial А4); and never under nothing counted, where «≈ 0 ₽» stood
 * over a spending of $50 with no rate (А3, handoff 157 v2 «Месяц без трат»).
 */
export function spentApprox(month: MoneyMonthView): Money | null {
  const { spent, spentIncome } = month
  return spentIncome && spent.minor !== 0n && spentIncome.currency !== spent.currency
    ? spentIncome
    : null
}

/**
 * The day's sum over its rows (MOL-184, В-1): what was counted — «≈» where a rate converted some of
 * it — and beside it what had no rate, in its own currency: «≈ 5 000 ֏ + 50 $», and «50 $» where
 * nothing was counted. A day of one $50 with no rate read «≈ 0 ֏» — nothing spent. Every figure is
 * the server's; the phone adds none up. A day only the phone knows of has none yet.
 */
export function dayTotalText(
  day: Pick<JournalDay, 'total' | 'estimated' | 'uncounted'>,
  locale: string,
): string | null {
  if (!day.total) return null
  const counted = day.total.minor !== 0n || day.uncounted.length === 0
  // A long sum breaks only after its «+»: «≈» stays with its figure, «+» never starts a line, and `Intl`
  // binds a figure to its sign already (MOL-184, round 2 at 320 px).
  // Whole only under «≈»: a day of one receipt of 5 403,12 ֏ says it over that very row (MOL-205, В-1 «в»).
  const total = day.estimated
    ? `≈\u00a0${formatEstimate(day.total, locale)}`
    : formatMoney(day.total, locale)
  return [
    ...(counted ? [total] : []),
    // As written, never rounded: there is no «≈» before it (adversarial А2 — «€6» over «€5.50»).
    ...day.uncounted.map((amount) => formatMoney(amount, locale)),
  ].join('\u00a0+ ')
}

/**
 * The spendings a waiting removal takes away: the last of «Удалить» and «Вернуть» about each is a
 * removal. One brought back with «Вернуть» behind a removal already tried is on screen again, as
 * the person was told (round 2, Н2).
 */
function beingRemoved(pending: readonly SpendingWrite[]): Set<string> {
  const last = new Map<string, 'remove' | 'restore'>()
  for (const write of pending)
    if (write.kind === 'remove' || write.kind === 'restore') last.set(write.id, write.kind)
  return new Set([...last].flatMap(([id, kind]) => (kind === 'remove' ? [id] : [])))
}

/** What the queue has to say about a row: on its way, amended, or refused. */
export type SpendingMark = 'waiting' | 'editing' | 'refused'

export type JournalRow =
  | {
      readonly kind: 'manual'
      readonly key: string
      readonly spending: SpendingView
      /** In the spending currency — the server's, and null for a row only the phone holds. */
      readonly counted: Money | null
      readonly mark: SpendingMark | null
      /** The refusal behind a «Не принята» mark, for the sheet to name. */
      readonly refusal: RejectedSpendingWrite | null
      /** Only on the phone yet: the sheet amends it through the queue, not over a revision. */
      readonly local: boolean
    }
  | {
      readonly kind: 'trip'
      readonly key: string
      readonly tripId: string
      readonly placeName: string
      readonly items: number
      readonly amount: Money
      readonly counted: Money | null
    }

export interface JournalDay {
  readonly day: string
  /** The server's total, or null for a day only the phone knows of — it has no figure yet. */
  readonly total: Money | null
  readonly estimated: boolean
  /** What of the day had no rate, in its own currencies — the server's, whole like `total`. */
  readonly uncounted: readonly Money[]
  readonly rows: JournalRow[]
}

/**
 * A spending only the phone holds, as the person last typed it: its record, or the latest
 * amendment waiting behind a record already on its way (review Т-4). The server has no figures of
 * it yet, so these are the only ones there are — unlike a server row, whose figures stay its own.
 */
function localView(
  write: Extract<SpendingWrite, { kind: 'record' }>,
  pending: readonly SpendingWrite[],
): SpendingView {
  const latest = pending
    .filter(
      (one): one is Extract<SpendingWrite, { kind: 'amend' }> =>
        one.kind === 'amend' && one.id === write.body.id,
    )
    .at(-1)?.body
  const { spentOn, amount, categoryId, note, place, accountId, debited } = latest ?? write.body
  const id = write.body.id
  return {
    id,
    spentOn,
    amount,
    categoryId,
    note: note ?? null,
    place: place ?? null,
    rate: null,
    accountId: accountId ?? null,
    debited: debited ?? null,
    transferId: null,
    revision: 1,
    amendedAt: null,
  }
}

/**
 * The month's journal with the queue laid over it (MOL-82, requirements 12–15): a spending still
 * on the phone stands at the top of its day marked «Отправляем…», an amendment not yet sent marks
 * the server's row — whose figures stay the server's until it answers — a removal hides the row,
 * and a refusal about a row on screen marks it «Не принята»: that row is where it is put right, and
 * any amendment of it takes the refusal away. A spending of a day the loaded pages have not reached
 * yet waits for them: shown there, it would stand among the wrong neighbours. A refusal with no row
 * on screen is `refusedRows`'s (MOL-159).
 */
export function journalOf(
  month: MoneyMonthView,
  pending: readonly SpendingWrite[],
  /** Trips whose removal is still on its way (MOL-76): their lines go, the figures stay the server's. */
  removedTrips: ReadonlySet<string> = new Set(),
  /** Spendings whose removal landed after this month was read (MOL-151): gone, not back for a moment. */
  gone: ReadonlySet<string> = new Set(),
  rejected: readonly RejectedSpendingWrite[] = [],
): JournalDay[] {
  const removing = beingRemoved(pending)
  const editing = new Set(pending.flatMap((write) => (write.kind === 'amend' ? [write.id] : [])))
  const refusals = new Map<string, RejectedSpendingWrite>()
  for (const item of rejected) {
    const id = spendingOf(item.write)
    if (id) refusals.set(id, item)
  }

  const days: JournalDay[] = month.days.map((day) => ({
    day: day.day,
    total: day.total,
    estimated: day.estimated,
    uncounted: day.uncounted,
    rows: day.entries.flatMap((entry): JournalRow[] => {
      if (entry.kind === 'trip')
        return removedTrips.has(entry.tripId)
          ? []
          : [
              {
                kind: 'trip',
                key: `${entry.tripId}:${entry.amount.currency}`,
                tripId: entry.tripId,
                placeName: entry.placeName,
                items: entry.items,
                amount: entry.amount,
                counted: entry.counted,
              },
            ]
      const { id } = entry.spending
      if (removing.has(id) || gone.has(id)) return []
      const refusal = refusals.get(id) ?? null
      return [
        {
          kind: 'manual',
          key: id,
          spending: entry.spending,
          counted: entry.counted,
          mark: refusal ? 'refused' : editing.has(id) ? 'editing' : null,
          refusal,
          local: false,
        },
      ]
    }),
  }))

  const known = new Set(days.flatMap((day) => day.rows.map((row) => row.key)))
  const reached = month.cursor === null ? '' : (month.days.at(-1)?.day ?? '')
  for (const write of pending) {
    if (write.kind !== 'record') continue
    const spending = localView(write, pending)
    if (known.has(spending.id) || removing.has(spending.id)) continue
    if (!spending.spentOn.startsWith(month.month) || spending.spentOn < reached) continue
    known.add(spending.id)
    const row: JournalRow = {
      kind: 'manual',
      key: spending.id,
      spending,
      counted: null,
      mark: 'waiting',
      refusal: null,
      local: true,
    }
    const at = days.findIndex((day) => day.day <= spending.spentOn)
    const same = days[at]
    if (same?.day === spending.spentOn) same.rows.unshift(row)
    else {
      const day: JournalDay = {
        day: spending.spentOn,
        total: null,
        estimated: false,
        uncounted: [],
        rows: [row],
      }
      if (at === -1) days.push(day)
      else days.splice(at, 0, day)
    }
  }
  return days.filter((day) => day.rows.length > 0)
}

/**
 * Every spending the server refused, as rows of its own — «Не приняты» on top of «Траты», whatever
 * the month and its pages (MOL-159); the screen leaves out one whose row the journal shows marked,
 * so a refusal stands once (adversarial round 6, П). Laid into the journal, a refusal stood only where the journal
 * guessed its row was: a page not loaded, a month not answered, another month, a date moved across
 * the month's edge, a spending removed elsewhere — each guess was a refusal left on a card whose one
 * action threw the typing away (adversarial rounds 1–5). Here nothing is guessed: each opens on
 * what was typed, and an amendment of a row the month has loaded goes again over the server's
 * revision; one of a row not loaded, over the revision it was made on.
 */
export function refusedRows(
  month: MoneyMonthView | null,
  rejected: readonly RejectedSpendingWrite[],
  pending: readonly SpendingWrite[],
): Extract<JournalRow, { kind: 'manual' }>[] {
  const loaded = new Map(
    (month?.days ?? []).flatMap((day) =>
      day.entries.flatMap((entry) =>
        entry.kind === 'manual' ? [[entry.spending.id, entry] as const] : [],
      ),
    ),
  )
  return rejected.flatMap((refusal): Extract<JournalRow, { kind: 'manual' }>[] => {
    const { write } = refusal
    if (write.kind === 'record')
      return [
        {
          kind: 'manual',
          key: write.body.id,
          spending: localView(write, pending),
          counted: null,
          mark: 'refused',
          refusal,
          local: true,
        },
      ]
    if (write.kind !== 'amend') return []
    const { id, body } = write
    const held = loaded.get(id)?.spending
    // The row says what was typed and refused; the revision is the server's when the month holds it.
    return [
      {
        kind: 'manual',
        key: id,
        spending: {
          id,
          spentOn: body.spentOn,
          amount: body.amount,
          categoryId: body.categoryId,
          note: body.note ?? null,
          place: body.place ?? null,
          rate: null,
          accountId: body.accountId === undefined ? (held?.accountId ?? null) : body.accountId,
          debited: body.debited ?? null,
          transferId: null,
          revision: held?.revision ?? body.revision,
          amendedAt: null,
        },
        counted: null,
        mark: 'refused',
        refusal,
        local: false,
      },
    ]
  })
}

/**
 * How many of the month's spendings wait on the phone — «Ещё не учтено: 1 трата отправляется»:
 * the card says it rather than adding them to a total it did not count. A removal not yet sent
 * counts too — the server's total still holds the spending it takes away.
 */
export function unsentIn(month: MoneyMonthView, pending: readonly SpendingWrite[]): number {
  const shown = new Set(
    month.days.flatMap((day) =>
      day.entries.flatMap((entry) => (entry.kind === 'manual' ? [entry.spending.id] : [])),
    ),
  )
  const removing = beingRemoved(pending)
  const ids = new Set<string>()
  for (const write of pending) {
    // A record the month already shows is counted — its answer was lost, not the spending
    // (adversarial Л) — and one being taken back is not waiting to be counted at all.
    if (
      write.kind === 'record' &&
      write.body.spentOn.startsWith(month.month) &&
      !shown.has(write.body.id) &&
      !removing.has(write.body.id)
    )
      ids.add(write.body.id)
    if (
      write.kind === 'amend' &&
      (write.body.spentOn.startsWith(month.month) || shown.has(write.id))
    )
      ids.add(write.id)
    if (write.kind === 'remove' && shown.has(write.id) && removing.has(write.id)) ids.add(write.id)
  }
  return ids.size
}

/**
 * «Продукты» of the owner, where a record with no purchases lands as a spending (MOL-78, В-1) —
 * none once removed from the choice: the sheet takes only a category it shows.
 */
export function groceriesOf(categories: readonly SpendingCategoryView[]): string | null {
  return (
    categories.find((category) => category.preset === TRIP_CATEGORY && !category.archived)?.id ??
    null
  )
}

/** A field behind «Дата, заметка, место» — the one its sheet opens on when it is wrong. */
export type SpendingDetailsField = 'day' | 'note' | 'place'

/**
 * The entry «Дата, заметка, место» of the spending sheet says what is behind it in one line (MOL-199,
 * Ф-17): «Сегодня · без заметки», «Вчера · Кофе с собой · «Кофеман»», «28 сент. · Кинза» — the day as a
 * card heads it (`dayWords`), then the note and the place as typed, never quoted by the line. A day the
 * picker cleared is no part; nothing typed besides it is `none`, «без заметки». «Nothing» is what draws
 * nothing (`drawsNothing`, the one list `INVISIBLE`): a U+200B pasted in left «Сегодня · » with a dot
 * hanging (adversarial А2).
 */
export function detailsLine(
  fields: { readonly day: string; readonly note: string; readonly place: string },
  locale: string,
  today: string,
  none: string,
): string {
  // «Вчера» beats the year; any other day of another year says it, a sheet having no month's caption (MOL-329 Ж1)
  const near = fields.day === today || fields.day === shiftDay(today, -1)
  const day = !isRateDay(fields.day)
    ? null
    : near
      ? dayWords(fields.day, locale, today)
      : calendarDayOfAnyYear(fields.day, locale, today)
  const typed = [fields.note, fields.place]
    .filter((part) => !drawsNothing(part))
    .map((part) => part.trim())
  return [day, ...(typed.length > 0 ? typed : [none])].filter((part) => part !== null).join(' · ')
}

/**
 * One operation as `OperationRow` draws it (Ф-12, MOL-176): the row knows no operation, only these
 * words and signs — put together here for «Траты» and in `accounts.ts` for an account, so every rule
 * of what a row says is a function a test reads. `amount` null — the row has no tail: its title says
 * the sum already (a check's reason).
 */
export interface OperationRowProps {
  readonly icon: Component
  /** A category's colour, or `muted` for what has none (Ф-4: the accent only where one presses). */
  readonly tint: string
  /** Read first, before everything the row says: «Открыть трату:». */
  readonly verb: string
  readonly title: string
  readonly meta: string
  readonly amount: string | null
  /** Under the amount: what it was counted as, or the operation's own money in another currency. */
  readonly sub: string | null
  readonly tag: { readonly tone: 'warn' | 'bad'; readonly text: string } | null
}

/** `t` of vue-i18n, as the functions of this module take it: a key, its values, a count. */
export type Translate = (key: string, named: Record<string, unknown>, plural?: number) => string

/**
 * A line under an amount breaks only between its parts — after «·» or between two amounts — never inside
 * the words of its last part: «25 000 ₽ · без» over ««списано»» (MOL-176, adversarial round 2, Б3). The
 * figures are whole already: `Intl` joins their groups and sign with no-break spaces.
 */
export function unbroken(text: string): string {
  const cut = text.lastIndexOf(' · ')
  const head = cut < 0 ? '' : text.slice(0, cut + ' · '.length)
  return head + text.slice(head.length).replaceAll(' ', '\u00a0')
}

/** Purchases are drawn in the colour of «Продукты», where they land (Ф-4) — never the accent. */
export function tripTint(groceries: SpendingCategoryView | null): string {
  return groceries ? categoryColour(groceries) : `var(--cat-${TRIP_CATEGORY})`
}

/** A spending's circle: its category's colour and icon, or no colour for one the phone does not know. */
export function spendingLook(category: SpendingCategoryView | null): {
  readonly icon: Component
  readonly tint: string
} {
  return category
    ? { icon: categoryIcon(category), tint: categoryColour(category) }
    : { icon: markRaw(IconTag), tint: 'muted' }
}

export const tripIcon = markRaw(IconCart)

/**
 * A line of the month's journal (MOL-82, handoff 01): a spending of one's own, or a finished trip's
 * purchases in one currency. The amount is as it was spent; under it, for another currency, what the
 * server counted it as — or that it could not. `when` is the day, where rows of different days stand
 * together — «Не приняты» (MOL-159).
 */
export function journalRowProps(
  row: JournalRow,
  context: {
    readonly t: Translate
    readonly locale: string
    readonly category: SpendingCategoryView | null
    readonly categoryName: string
    readonly spendCurrency: Currency
    readonly when?: string
  },
): OperationRowProps {
  const { t, locale, category, categoryName } = context
  const trip = row.kind === 'trip'
  const money = trip ? row.amount : row.spending.amount

  let line: string
  // a trip in the month has money: with no purchase it is a receipt's sum (MOL-78, MOL-227)
  if (trip && row.items === 0) line = t('spending.trip_row_sum', { category: categoryName })
  else if (trip)
    line = t('spending.trip_row_meta', { category: categoryName, n: row.items }, row.items)
  else {
    const { note, place } = row.spending
    // Without «что это» the title is already the category: the line under it is the place alone.
    line = note === null ? (place ?? '') : place ? `${categoryName} · ${place}` : categoryName
  }

  let sub: string | null = null
  // Not counted yet is not «не посчитано» — that says no rate of the day was known; a row still on
  // the phone says «Отправляем…» already (review Т-8).
  if (money.currency !== context.spendCurrency && !(row.kind === 'manual' && row.local))
    sub = row.counted
      ? `≈\u00a0${formatEstimate(row.counted, locale)}`
      : unbroken(t('spending.uncounted_row', {}))

  const mark = row.kind === 'manual' ? row.mark : null
  const look = trip ? { icon: tripIcon, tint: tripTint(category) } : spendingLook(category)
  return {
    ...look,
    verb: t(trip ? 'spending.row_open_trip' : 'spending.row_open', {}),
    title: trip
      ? t('spending.trip_row_title', { place: row.placeName })
      : row.spending.transferId !== null
        ? // A transfer's fee says what it is (MOL-253, Р-1): «Прочее · комиссия за перевод».
          t('transfer.fee_spending', {})
        : (row.spending.note ?? categoryName),
    meta: [context.when, line].filter(Boolean).join(' · '),
    amount: formatMoney(money, locale),
    sub,
    tag:
      mark === null
        ? null
        : mark === 'refused'
          ? { tone: 'bad', text: t('spending.refused', {}) }
          : {
              tone: 'warn',
              text: t(mark === 'editing' ? 'spending.editing' : 'spending.pending', {}),
            },
  }
}

/** What the sheet of a spending is opened on: a new one, one of one's own, or a trip's line. */
export type SpendingTarget =
  /** `prefill`: a record with no purchases handed over as a spending (MOL-78, В-1). */
  | { readonly kind: 'add'; readonly prefill?: SpendingPrefill }
  | { readonly kind: 'manual'; readonly row: Extract<JournalRow, { kind: 'manual' }> }
  | {
      readonly kind: 'trip'
      readonly row: Extract<JournalRow, { kind: 'trip' }>
      readonly day: string
    }

/** What «Удалить» hands the screen for its strip: «Вернуть» and the words for it. */
export interface Removed {
  readonly undo: SpendingUndo
  readonly title: string
  readonly amount: string
}

/**
 * Money that is there and that whole units would print as a nought: under half a unit, either sign,
 * the unit the currency's own (review 11 of MOL-117). Such a figure is printed as typed instead.
 */
export function roundsToNought(value: Money): boolean {
  const size = value.minor < 0n ? -value.minor : value.minor
  return size !== 0n && size * 2n < 10n ** BigInt(MINOR_EXPONENT[value.currency])
}

/**
 * A figure of «Бюджет» (MOL-117) with its sign left to the words around it — «сверх плана», «осталось»:
 * whole units, as every figure of «Деньги», unless that prints a nought for money that is there —
 * 0,40 ֏ over the plan is «0,40 ֏», never «0 ֏» (adversarial Г).
 */
export function budgetAmount(value: Money, locale: string): string {
  const size = { ...value, minor: value.minor < 0n ? -value.minor : value.minor }
  return roundsToNought(size) ? formatMoney(size, locale) : formatEstimate(size, locale)
}
